/**
 * Standard column sizes for consistent table layouts across the project
 *
 * Usage example:
 * ```typescript
 * import { ColumnSize } from '@aibees/shared/utils';
 *
 * const columns = [
 *   {
 *     key: 'name',
 *     label: 'Company Name',
 *     size: 'large', // 312px
 *     render: (_, v) => <span>{v.name}</span>,
 *   },
 *   {
 *     key: 'status',
 *     label: 'Status',
 *     size: 'small', // 152px
 *     render: (_, v) => <Badge>{v.status}</Badge>,
 *   },
 *   {
 *     key: 'description',
 *     label: 'Description',
 *     size: 'medium', // 184px
 *     render: (_, v) => <Text>{v.description}</Text>,
 *   },
 * ];
 * ```
 */
export const COLUMN_SIZES = {
  xsmall: 120,
  small: 152, // For compact columns like actions, status, icons
  medium: 184, // For moderate content like dates, categories, short text
  large: 312, // For wide content like names, descriptions, complex data
} as const;

export type ColumnSize = keyof typeof COLUMN_SIZES;

/**
 * Helper function to get column width by size
 * @param size - The size key (small, medium, large)
 * @returns The pixel width value
 */
export const getColumnWidth = (size: ColumnSize): number => COLUMN_SIZES[size];

/**
 * Type guard to check if a value is a valid column size
 * @param value - The value to check
 * @returns True if the value is a valid column size
 */
export const isValidColumnSize = (value: any): value is ColumnSize => {
  return typeof value === 'string' && value in COLUMN_SIZES;
};
